import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useUpdatePaymentsSettings } from '~entities/studios/api/update-payments-settings';
import { PaymentProcessingMethod } from '~entities/studios/model/types';

import { AllowedPaymentMethodsSettings } from '~features/payment/ui/allowed-payment-methods-settings';
import {
  CardOnFilePermissiveWarningModal,
  type CardOnFileWarningVariant,
} from '~features/payment/ui/card-on-file-permissive-warning-modal';
import {
  PaymentProcessingJourneyModal,
  type PaymentProcessingJourneyVariant,
} from '~features/payment/ui/payment-processing-journey-modal';

import { KEYS } from '~shared/api/query-keys';
import { Button } from '~shared/shadcn/ui/button';
import { Switch } from '~shared/shadcn/ui/switch';

export const PaymentSettings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: studioDetails, isFetching } = useGetStudioDetails();
  const [paymentProcessing, setPaymentProcessing] = useState<PaymentProcessingMethod>(PaymentProcessingMethod.AUTO);
  const [journeyModalVariant, setJourneyModalVariant] = useState<PaymentProcessingJourneyVariant | null>(null);
  const [billingProration, setBillingProration] = useState<
    'full' | 'first' | 'both'
  >('full');
  const [paymentRetries, setPaymentRetries] = useState<number>(3);
  const [collectPaymentMethod, setCollectPaymentMethod] = useState<boolean>(
    studioDetails?.collectPaymentMethod ?? true,
  );
  const [
    requirePaymentMethodForPaidEnrollments,
    setRequirePaymentMethodForPaidEnrollments,
  ] = useState<boolean>(
    studioDetails?.requirePaymentMethodForPaidEnrollments !== false,
  );
  const [
    retainPaymentMethodWhileEnrolled,
    setRetainPaymentMethodWhileEnrolled,
  ] = useState<boolean>(
    studioDetails?.retainPaymentMethodWhileEnrolled ?? false,
  );
  const [cardOnFileWarningVariant, setCardOnFileWarningVariant] =
    useState<CardOnFileWarningVariant | null>(null);
  const [prorationEnabled, setProrationEnabled] = useState<boolean>(
    studioDetails?.prorationEnabled ?? true,
  );
  const [prorationBasis, setProrationBasis] = useState<'calendar' | 'session'>(
    studioDetails?.prorationBasis === 'session' ? 'session' : 'calendar',
  );
  const [combineHourlyMinutes, setCombineHourlyMinutes] = useState<boolean>(
    studioDetails?.combineHourlyMinutesForBestRate ?? false,
  );
  const [combineAcrossSiblings, setCombineAcrossSiblings] = useState<boolean>(
    studioDetails?.combineHourlyMinutesAcrossSiblings ?? false,
  );
  const [consistentMonthlyHourlyTuition, setConsistentMonthlyHourlyTuition] =
    useState<boolean>(studioDetails?.consistentMonthlyHourlyTuition ?? false);

  const [paymentRunPostponeCronEnabled, setPaymentRunPostponeCronEnabled] =
    useState<boolean>(
      studioDetails?.paymentRunPostponeCronEnabled ?? false,
    );
  const [postTuitionToParentAccount, setPostTuitionToParentAccount] =
    useState<boolean>(studioDetails?.postTuitionToParentAccount ?? false);

  const [allowSelectiveInvoicePayment, setAllowSelectiveInvoicePayment] =
    useState<boolean>(studioDetails?.allowSelectiveInvoicePayment ?? true);
  const [allowPartialInvoicePayment, setAllowPartialInvoicePayment] =
    useState<boolean>(studioDetails?.allowPartialInvoicePayment ?? false);

  useEffect(() => {
    if (studioDetails) {
      // Normalize: both 'auto' and 'subscription' mean Auto Process; only 'manual' is manual
      const method = studioDetails.paymentProcessingMethod;
      setPaymentProcessing(
        method === PaymentProcessingMethod.MANUAL
          ? PaymentProcessingMethod.MANUAL
          : PaymentProcessingMethod.AUTO,
      );
      setCollectPaymentMethod(studioDetails.collectPaymentMethod);
      setRequirePaymentMethodForPaidEnrollments(
        studioDetails.collectPaymentMethod
          ? true
          : studioDetails.requirePaymentMethodForPaidEnrollments !== false,
      );
      setRetainPaymentMethodWhileEnrolled(
        studioDetails.retainPaymentMethodWhileEnrolled ?? false,
      );
      setProrationEnabled(studioDetails.prorationEnabled ?? true);
      setProrationBasis(
        studioDetails.prorationBasis === 'session' ? 'session' : 'calendar',
      );
      setCombineHourlyMinutes(
        studioDetails.combineHourlyMinutesForBestRate ?? false,
      );
      setCombineAcrossSiblings(
        !!studioDetails.combineHourlyMinutesForBestRate &&
          !!studioDetails.combineHourlyMinutesAcrossSiblings,
      );
      setConsistentMonthlyHourlyTuition(
        studioDetails.consistentMonthlyHourlyTuition ?? false,
      );
      setPaymentRunPostponeCronEnabled(
        studioDetails.paymentRunPostponeCronEnabled ?? false,
      );
      setPostTuitionToParentAccount(
        studioDetails.postTuitionToParentAccount ?? false,
      );
      setAllowSelectiveInvoicePayment(
        studioDetails.allowSelectiveInvoicePayment ?? true,
      );
      setAllowPartialInvoicePayment(
        studioDetails.allowPartialInvoicePayment ?? false,
      );

      const prorationMap: Record<string, 'full' | 'first' | 'both'> = {
        full_month_always: 'full',
        first_month_only: 'first',
        first_and_last_month: 'both',
      };
      setBillingProration(prorationMap[studioDetails.prorationMode] ?? 'full');
      // Ensure number is always defined so the controlled input never gets value={undefined}
      setPaymentRetries(studioDetails.failedPaymentRetryCount || 3);
    }
  }, [studioDetails]);

  const updateSettingsMutation = useUpdatePaymentsSettings();

  // Shared mutation error handler. The server may reject the change for
  // many reasons (e.g. trying to flip combine-minutes while unposted
  // drafts exist). Rather than rolling back individual fields and risking
  // a UI that drifts from the server's canonical record, invalidate the
  // studio-details query — the useEffect above will then re-sync every
  // local state variable from the refetched data once it lands.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMutationError = (error: any) => {
    const message =
      error?.response?.data?.message ??
      'Failed to update payment settings. Please try again.';
    toast.error(message);
    void queryClient.invalidateQueries({ queryKey: [KEYS.STUDIO_DETAILS] });
  };

  const buildSettingsPayload = (paymentMethod: PaymentProcessingMethod) => {
    const mappedProrationMode: Record<'full' | 'first' | 'both', string> = {
      full: 'full_month_always',
      first: 'first_month_only',
      both: 'first_and_last_month',
    };
    const paymentMethodToSend =
      paymentMethod === PaymentProcessingMethod.MANUAL
        ? PaymentProcessingMethod.MANUAL
        : PaymentProcessingMethod.AUTO;
    return {
      paymentProcessingMethod: paymentMethodToSend,
      prorationMode: mappedProrationMode[billingProration],
      prorationEnabled,
      prorationBasis,
      failedPaymentRetryCount: paymentRetries,
      collectPaymentMethod,
      requirePaymentMethodForPaidEnrollments: collectPaymentMethod
        ? true
        : requirePaymentMethodForPaidEnrollments,
      retainPaymentMethodWhileEnrolled,
      combineHourlyMinutesForBestRate: combineHourlyMinutes,
      combineHourlyMinutesAcrossSiblings: combineHourlyMinutes
        ? combineAcrossSiblings
        : false,
      consistentMonthlyHourlyTuition,
      allowSelectiveInvoicePayment,
      allowPartialInvoicePayment,
      // Postpone toggle is Manual-only (ENR-2162). Auto always saves off.
      paymentRunPostponeCronEnabled:
        paymentMethodToSend === PaymentProcessingMethod.MANUAL
          ? paymentRunPostponeCronEnabled
          : false,
      // ENR-3918: Manual-only. Auto always saves off (auto already promotes).
      postTuitionToParentAccount:
        paymentMethodToSend === PaymentProcessingMethod.MANUAL
          ? postTuitionToParentAccount
          : false,
    };
  };

  const handlePreviewInvoices = () => {
    navigate({
      to: '/payment/invoices',
      search: { statuses: 'upcoming,scheduled' },
    });
  };

  const isJourneySelectionChange =
    journeyModalVariant !== null &&
    ((journeyModalVariant === 'auto' &&
      paymentProcessing === PaymentProcessingMethod.MANUAL) ||
      (journeyModalVariant === 'manual' &&
        paymentProcessing !== PaymentProcessingMethod.MANUAL));

  const handleJourneyConfirm = () => {
    if (journeyModalVariant === null) return;
    const method =
      journeyModalVariant === 'auto'
        ? PaymentProcessingMethod.AUTO
        : PaymentProcessingMethod.MANUAL;
    const isAuto = journeyModalVariant === 'auto';
    const wasSelectionChange = isJourneySelectionChange;

    setJourneyModalVariant(null);
    if (!wasSelectionChange) {
      return;
    }

    setPaymentProcessing(method);
    if (isAuto) {
      // Post-tuition is Manual-only; clear local state when leaving Manual.
      setPostTuitionToParentAccount(false);
    } else {
      // Manual Review relies on Payment Run Review Approve & Lock.
      setPaymentRunPostponeCronEnabled(true);
    }

    updateSettingsMutation.mutate(buildSettingsPayload(method), {
      onSuccess: () => {
        toast.success('Your payment and billing settings have been updated.');
        if (isAuto) {
          navigate({
            to: '/payment/invoices',
            search: { statuses: 'upcoming,scheduled' },
          });
        } else {
          navigate({ to: '/payment/payment-run-review' });
        }
      },
      onError: handleMutationError,
    });
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(buildSettingsPayload(paymentProcessing), {
      onSuccess: () => {
        toast.success('Your payment and billing settings have been updated.');
      },
      onError: handleMutationError,
    });
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <div>
      <Link to='/dashboard' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Tuition settings</h2>
      </Link>
      <div className='mt-8 border-t' />
      {isFetching ? (
        <div className='flex h-56 items-center justify-center'>
          <Loader2 className='size-8 animate-spin' />
        </div>
      ) : (
        <motion.div
          className='container min-w-0 max-w-[680px] space-y-10 py-6 sm:py-10'
          initial='hidden'
          animate='show'
          variants={container}
        >
          <motion.div className='space-y-2' variants={item}>
            <h1 className='text-3xl font-bold tracking-tight'>
              Payment & Billing Settings
            </h1>
            <p className='text-muted-foreground'>
              Manage how payments are processed and how billing is handled.
            </p>
          </motion.div>

          <motion.div className='space-y-8' variants={item}>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>Payment Processing</h2>
                <p className='text-sm text-muted-foreground'>
                  Choose how you want payments to be processed
                </p>
              </div>

              <div className='space-y-3'>
                <OptionCard
                  id='auto-process'
                  value={PaymentProcessingMethod.AUTO}
                  currentValue={paymentProcessing}
                  title='Auto Process Payments'
                  description='Upcoming & Scheduled payments will be processed automatically.'
                  defaultOption
                  onChange={() => setJourneyModalVariant('auto')}
                />

                <OptionCard
                  id='manual-review'
                  value={PaymentProcessingMethod.MANUAL}
                  currentValue={paymentProcessing}
                  title='Manual Review Before Processing'
                  description='Payments require verification before processing.'
                  onChange={() => setJourneyModalVariant('manual')}
                />
              </div>

              {journeyModalVariant !== null && (
                <PaymentProcessingJourneyModal
                  variant={journeyModalVariant}
                  open={true}
                  onOpenChange={(open) => !open && setJourneyModalVariant(null)}
                  onConfirm={handleJourneyConfirm}
                  isSelectionChange={isJourneySelectionChange}
                  onPreviewInvoices={handlePreviewInvoices}
                />
              )}
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>Payment Retries</h2>
                <p className='text-sm text-muted-foreground'>
                  Choose how many times you want payments to be retried
                </p>
              </div>

              <div className='space-y-3'>
                <div className='cursor-pointer rounded-lg bg-amber-50 p-5 shadow-sm transition-all duration-200 hover:scale-[1.01]'>
                  <div className='space-y-3'>
                    <div className='space-y-1'>
                      <div className='flex items-center gap-2'>
                        <label className='text-base font-medium'>
                          Number of Retry Attempts
                        </label>
                      </div>
                      <p className='text-xs text-gray-500'>
                        <b>Note:</b> Failed payments are automatically retried
                        every 3 days, for the number of attempts specified below.
                      </p>
                    </div>

                    <div className='flex items-center gap-3'>
                      <label
                        htmlFor='retry-count'
                        className='text-sm font-medium text-gray-700'
                      >
                        Retry attempts:
                      </label>
                      <input
                        id='retry-count'
                        type='number'
                        min='1'
                        max='10'
                        value={paymentRetries}
                        onChange={(e) => {
                          setPaymentRetries(
                            Math.max(
                              1,
                              Math.min(10, Number.parseInt(e.target.value) || 1),
                            ),
                          );
                        }}
                        className='w-20 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400'
                      />
                      <span className='text-xs text-gray-500'>
                        (1-10 attempts)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <motion.div variants={item}>
              <AllowedPaymentMethodsSettings
                locationId={studioDetails?.locationId ?? ''}
                isV2={studioDetails?.isV2 === true}
              />
            </motion.div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>Billing & Proration</h2>
                <p className='text-sm text-muted-foreground'>
                  Configure how billing cycles and proration are handled
                </p>
              </div>

              <div className='space-y-3'>
                {/* Proration Toggle */}
                <div
                  className={`rounded-lg p-5 transition-all duration-200 ${prorationEnabled ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'
                    }`}
                >
                  <div className='flex items-start justify-between gap-4'>
                    <div className='space-y-1'>
                      <div className='flex items-center gap-2'>
                        <label className='text-base font-medium'>
                          Enable Proration
                        </label>
                        <span className='rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
                          Default
                        </span>
                      </div>
                      <p className='text-xs text-gray-500'>
                        When enabled, tuition is calculated based on the remaining
                        days in the billing cycle. When disabled, full tuition is
                        always charged regardless of enrollment date.
                      </p>
                      {!prorationEnabled && (
                        <p className='mt-2 text-xs font-medium text-amber-600'>
                          Proration is disabled. All enrollments and class
                          transfers will charge full tuition without proration.
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={prorationEnabled}
                      onCheckedChange={setProrationEnabled}
                    />
                  </div>
                  {prorationEnabled ? (
                    <label className='mt-4 block text-sm'>
                      Monthly proration basis
                      <select
                        className='mt-1 block w-full max-w-sm rounded-md border bg-white px-3 py-2'
                        value={prorationBasis}
                        onChange={(event) => {
                          setProrationBasis(
                            event.target.value === 'session'
                              ? 'session'
                              : 'calendar',
                          );
                        }}
                      >
                        <option value='calendar'>Calendar days</option>
                        <option value='session'>
                          Scheduled class meetings (monthly)
                        </option>
                      </select>
                      <span className='mt-1 block text-xs text-gray-500'>
                        Weekly and one-time charges stay on their existing
                        rules. This choice applies to new monthly charges.
                      </span>
                    </label>
                  ) : null}
                </div>

                <OptionCard
                  id='full-month'
                  value='full'
                  currentValue={billingProration}
                  onChange={(value) => {
                    setBillingProration(value as 'full' | 'first' | 'both');
                  }}
                  title='Charge Full Billing Cycle Always'
                  description='Full fee charged for the selected billing cycle (weekly, bi-weekly, monthly, etc.). Proration only applies if a student joins mid-cycle'
                  defaultOption
                />

                {/* <OptionCard
                id='first-month'
                value='first'
                currentValue={billingProration}
                onChange={setBillingProration}
                title='Prorate First Month Only'
                description="First month's fee is prorated."
              />

              <OptionCard
                id='both-months'
                value='both'
                currentValue={billingProration}
                onChange={setBillingProration}
                title='Prorate First and Last Month'
                description='Both first and last month fees are prorated.'
              /> */}
              </div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>Hourly rate table</h2>
                <p className='text-sm text-muted-foreground'>
                  Combine minutes on the same chart for a better band. Nested
                  sibling combine pools a family and equal-splits the total.
                </p>
                <p className='text-sm text-muted-foreground'>
                  <Link
                    to='/payment/hourly-tuition'
                    className='font-medium text-custom-secondary-1 underline-offset-2 hover:underline'
                  >
                    Post hourly tuition
                  </Link>
                  {' · '}
                  <Link
                    to='/payment/hourly-tuition/settings'
                    className='font-medium text-custom-secondary-1 underline-offset-2 hover:underline'
                  >
                    Manage hourly charts
                  </Link>
                </p>
              </div>

              <div
                className={`rounded-lg p-5 transition-all duration-200 ${
                  combineHourlyMinutes ? 'bg-gray-50 shadow-sm' : 'bg-gray-50'
                }`}
              >
                <div className='flex items-start justify-between gap-4'>
                  <div className='space-y-1'>
                    <label className='text-base font-medium'>
                      Combine minutes for best rate
                    </label>
                    <p className='text-xs text-gray-500'>
                      Sum a student&rsquo;s weekly minutes across classes on the
                      same chart, then bill the combined band.
                    </p>
                    <p className='mt-2 text-xs text-gray-500'>
                      Cannot change while unposted hourly drafts exist.
                    </p>
                  </div>
                  <Switch
                    checked={combineHourlyMinutes}
                    onCheckedChange={(checked) => {
                      setCombineHourlyMinutes(checked);
                      if (!checked) setCombineAcrossSiblings(false);
                    }}
                  />
                </div>
              </div>

              {combineHourlyMinutes && (
                <div className='ml-4 rounded-lg border border-dashed bg-white p-5'>
                  <div className='flex items-start justify-between gap-4'>
                    <div className='space-y-1'>
                      <label className='text-base font-medium'>
                        Combine minutes across siblings in a family
                      </label>
                      <p className='text-xs text-gray-500'>
                        Pool siblings on the same chart and equal-split the
                        family band.
                      </p>
                    </div>
                    <Switch
                      checked={combineAcrossSiblings}
                      onCheckedChange={setCombineAcrossSiblings}
                    />
                  </div>
                </div>
              )}

              <motion.div
                variants={item}
                className={`rounded-lg p-5 transition-all duration-200 ${
                  consistentMonthlyHourlyTuition
                    ? 'bg-gray-50 shadow-sm'
                    : 'bg-gray-50'
                }`}
              >
                <motion.div className='flex items-start justify-between gap-4' variants={item}>
                  <motion.div className='space-y-1' variants={item}>
                    <motion.div className='flex items-center gap-2' variants={item}>
                      <label
                        htmlFor='consistent-monthly-tuition'
                        className='text-base font-medium'
                      >
                        Consistent monthly hourly tuition
                      </label>
                    </motion.div>
                    <p className='text-xs text-gray-500'>
                      Cap billed weeks at four per calendar month so invoices
                      stay consistent.
                    </p>
                    <p className='mt-2 text-xs text-gray-500'>
                      Cannot change while unposted hourly drafts exist.
                    </p>
                  </motion.div>
                  <Switch
                    id='consistent-monthly-tuition'
                    checked={consistentMonthlyHourlyTuition}
                    onCheckedChange={setConsistentMonthlyHourlyTuition}
                  />
                </motion.div>
              </motion.div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4' id='family-class-count'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Family class-count (monthly)
                </h2>
                <p className='text-sm text-muted-foreground'>
                  Monthly classes only; equal-split across siblings. Does not
                  apply to hourly.
                </p>
                <p className='text-sm text-muted-foreground'>
                  <Link
                    to='/payment/class-count-tuition'
                    className='font-medium text-custom-secondary-1 underline-offset-2 hover:underline'
                  >
                    Manage family class-count charts
                  </Link>
                </p>
              </div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4' id='enrollment-timing-hourly'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Enrollment timing (hourly)
                </h2>
                <p className='text-sm text-muted-foreground'>
                  First-month and last-month hourly charges are set per session,
                  not here.
                </p>
                <p className='text-sm text-muted-foreground'>
                  <Link
                    to='/sessions'
                    className='font-medium text-custom-secondary-1 underline-offset-2 hover:underline'
                  >
                    Open sessions
                  </Link>
                </p>
              </div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Payment Run Review Settings
                </h2>
              </div>
              {paymentProcessing === PaymentProcessingMethod.MANUAL ? (
                <div
                  className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${paymentRunPostponeCronEnabled ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'}`}
                >
                  <div className='space-y-1'>
                    <div className='flex items-center gap-2'>
                      <label
                        htmlFor='payment-run-postpone'
                        className='text-base font-medium'
                      >
                        Apply postponement in scheduled processing
                      </label>
                      {!paymentRunPostponeCronEnabled ? (
                        <span className='rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
                          Off by default
                        </span>
                      ) : null}
                    </div>
                    <p className='text-xs text-gray-500'>
                      Controls only the Postpone action on Payment Run Review.
                      Approve &amp; Lock and Unlock stay available either way.
                    </p>
                  </div>
                  <Switch
                    id='payment-run-postpone'
                    checked={paymentRunPostponeCronEnabled}
                    onCheckedChange={setPaymentRunPostponeCronEnabled}
                  />
                </div>
              ) : (
                <p className='rounded-lg bg-gray-50 p-5 text-xs text-gray-500'>
                  Postpone is available on Manual Review only. Approve &amp;
                  Lock and Unlock remain available on Automatic payment
                  processing.
                </p>
              )}

              <div
                className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${
                  paymentProcessing !== PaymentProcessingMethod.MANUAL
                    ? 'bg-gray-50 opacity-80'
                    : postTuitionToParentAccount
                      ? 'bg-amber-50 shadow-sm'
                      : 'bg-gray-50'
                }`}
              >
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <label
                      htmlFor='post-tuition-to-parent-account'
                      className='text-base font-medium'
                    >
                      Post tuition to parent&apos;s account
                    </label>
                    {paymentProcessing !== PaymentProcessingMethod.MANUAL ? (
                      <span className='rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800'>
                        Requires Manual Review
                      </span>
                    ) : !postTuitionToParentAccount ? (
                      <span className='rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
                        Off by default
                      </span>
                    ) : null}
                  </div>
                  {paymentProcessing !== PaymentProcessingMethod.MANUAL ? (
                    <p className='text-xs text-amber-800'>
                      Switch Payment Processing to Manual Review before turning
                      this on. Automatic payment processing already posts and
                      charges on the due date.
                    </p>
                  ) : (
                    <p className='text-xs text-gray-500'>
                      On the invoice due date, Upcoming invoices become Invoice
                      Scheduled so tuition posts to the parent&apos;s account.
                      While this is on, saved payment methods are never charged
                      automatically, including after Approve &amp; Lock. Collect
                      payment from the parent portal or in studio.
                    </p>
                  )}
                </div>
                <Switch
                  id='post-tuition-to-parent-account'
                  checked={
                    paymentProcessing === PaymentProcessingMethod.MANUAL &&
                    postTuitionToParentAccount
                  }
                  disabled={
                    paymentProcessing !== PaymentProcessingMethod.MANUAL
                  }
                  onCheckedChange={setPostTuitionToParentAccount}
                />
              </div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Parent Invoice Payments
                </h2>
                <p className='text-sm text-muted-foreground'>
                  Controls how families pay their charges from the Parent
                  Portal. These also apply when your staff takes a payment.
                </p>
              </div>

              <div
                className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${allowSelectiveInvoicePayment ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'}`}
              >
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <label
                      htmlFor='allow-selective-invoice-payment'
                      className='text-base font-medium'
                    >
                      Let families choose which charges to pay
                    </label>
                    <span className='rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
                      On by default
                    </span>
                  </div>
                  <p className='text-xs text-gray-500'>
                    When enabled, a parent can select individual charges,
                    including upcoming ones they want to pay early. When
                    disabled, a payment always covers the oldest due charges
                    first so past due is cleared before future charges.
                  </p>
                </div>
                <Switch
                  id='allow-selective-invoice-payment'
                  checked={allowSelectiveInvoicePayment}
                  onCheckedChange={setAllowSelectiveInvoicePayment}
                />
              </div>

              <div
                className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${allowPartialInvoicePayment ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'}`}
              >
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <label
                      htmlFor='allow-partial-invoice-payment'
                      className='text-base font-medium'
                    >
                      Allow partial payments on a charge
                    </label>
                    <span className='rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
                      Off by default
                    </span>
                  </div>
                  <p className='text-xs text-gray-500'>
                    When enabled, a payment may be less than a charge&rsquo;s
                    remaining balance and the charge stays partially paid.
                    When disabled, each selected charge must be paid in full.
                  </p>
                </div>
                <Switch
                  id='allow-partial-invoice-payment'
                  checked={allowPartialInvoicePayment}
                  onCheckedChange={setAllowPartialInvoicePayment}
                />
              </div>
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Parent Portal card-on-file
                </h2>
                <p className='text-sm text-muted-foreground'>
                  Controls whether families must add a payment method during
                  Parent Portal enrollment (V2). Studio Portal staff can still
                  skip a payment method per enrollment.
                </p>
              </div>

              <div
                className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${
                  collectPaymentMethod ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'
                }`}
              >
                <div className='space-y-1'>
                  <p className='font-medium'>
                    Require payment method for $0 enrollments
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    When on, parents cannot skip a card for free classes,
                    scholarships, or enrollments with nothing due at checkout.
                    Turning this on also requires a payment method for paid
                    enrollments.
                  </p>
                </div>
                <Switch
                  id='require-pm-zero'
                  checked={collectPaymentMethod}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setCollectPaymentMethod(true);
                      setRequirePaymentMethodForPaidEnrollments(true);
                      return;
                    }
                    setCardOnFileWarningVariant('zero');
                  }}
                />
              </div>

              <div
                className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${
                  requirePaymentMethodForPaidEnrollments
                    ? 'bg-amber-50 shadow-sm'
                    : 'bg-gray-50'
                } ${collectPaymentMethod ? 'opacity-70' : ''}`}
              >
                <div className='space-y-1'>
                  <p className='font-medium'>
                    Require payment method for paid enrollments
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    When on, parents must add a card when money is due at
                    checkout. When off, they may enroll with an unpaid balance
                    (you collect later). Locked on while $0 require is enabled.
                  </p>
                </div>
                <Switch
                  id='require-pm-paid'
                  checked={
                    collectPaymentMethod
                      ? true
                      : requirePaymentMethodForPaidEnrollments
                  }
                  disabled={collectPaymentMethod}
                  onCheckedChange={(checked) => {
                    if (collectPaymentMethod) return;
                    if (checked) {
                      setRequirePaymentMethodForPaidEnrollments(true);
                      return;
                    }
                    setCardOnFileWarningVariant('paid');
                  }}
                />
              </div>

              <CardOnFilePermissiveWarningModal
                open={cardOnFileWarningVariant !== null}
                variant={cardOnFileWarningVariant ?? 'zero'}
                onOpenChange={(open) => {
                  if (!open) setCardOnFileWarningVariant(null);
                }}
                onConfirm={() => {
                  if (cardOnFileWarningVariant === 'paid') {
                    setRequirePaymentMethodForPaidEnrollments(false);
                    return;
                  }
                  setCollectPaymentMethod(false);
                }}
              />
            </div>

            <div className='my-6 h-px w-full bg-gray-100' />

            <div className='space-y-4'>
              <div className='space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Payment method retention (active enrollments)
                </h2>
                <p className='text-sm text-muted-foreground'>
                  Prevents parents from deleting their only payment method
                  while they still have active or scheduled enrollments. They
                  can add a replacement first, then remove the old one. Other
                  studios are unaffected unless this is enabled.
                </p>
              </div>

              <div
                className={`flex items-start justify-between gap-4 rounded-lg p-5 transition-all duration-200 ${retainPaymentMethodWhileEnrolled ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'}`}
              >
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <label
                      htmlFor='retain-payment-method-while-enrolled'
                      className='text-base font-medium'
                    >
                      Prevent parents from removing their only payment method
                      while they have active enrollments
                    </label>
                    <span className='rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
                      Off by default
                    </span>
                  </div>
                  <p className='text-xs text-gray-500'>
                    When enabled, enrolled families must add an updated
                    payment method before removing the last one on file. When
                    disabled, parents can remove saved payment methods as they
                    do today.
                  </p>
                </div>
                <Switch
                  id='retain-payment-method-while-enrolled'
                  checked={retainPaymentMethodWhileEnrolled}
                  onCheckedChange={setRetainPaymentMethodWhileEnrolled}
                />
              </div>
            </div>
          </motion.div>

          <motion.div variants={item}>
            <Button
              className='w-full rounded-lg bg-primary py-6'
              onClick={handleSaveSettings}
              disabled={updateSettingsMutation.isPending}
            >
              {updateSettingsMutation.isPending && (
                <Loader2 className='mr-2 size-4 animate-spin' />
              )}
              Save Settings
            </Button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

interface OptionCardProps {
  id: string;
  value: string;
  currentValue: string;
  onChange?: (value: string) => void;
  title: string;
  description: string;
  defaultOption?: boolean;
  recommended?: boolean;
  disabled?: boolean;
}

function OptionCard({
  id,
  value,
  currentValue,
  onChange,
  title,
  description,
  defaultOption,
  recommended,
  disabled,
}: OptionCardProps) {
  const isSelected = value === currentValue;

  return (
    <div
      onClick={() => {
        if (onChange && !disabled) {
          onChange(value);
        }
      }}
      className={`cursor-pointer rounded-lg p-5 transition-all duration-200 ${isSelected ? 'bg-amber-50 shadow-sm' : 'bg-gray-50 hover:bg-gray-100'
        } hover:scale-[1.01] ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <div className='flex items-start gap-3'>
        <div
          className={`mt-1 flex size-5 items-center justify-center rounded-full border ${isSelected ? 'border-amber-400 bg-white' : 'border-gray-300'} `}
        >
          {isSelected && <div className='size-3 rounded-full bg-amber-400' />}
        </div>

        <div className='space-y-1'>
          <div className='flex items-center gap-2'>
            <label
              htmlFor={id}
              className='cursor-pointer text-base font-medium'
            >
              {title}
            </label>
            {recommended && (
              <span className='rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700'>
                Recommended
              </span>
            )}
            {defaultOption && (
              <span className='rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500'>
                Default
              </span>
            )}
          </div>
          <p className='text-xs text-gray-500'>{description}</p>
        </div>
      </div>
    </div>
  );
}
